import { useMutation } from '@apollo/client';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography
} from '@mui/material';
import {
  CreateEmailChangeRequest,
  UpdatePersonEmail
} from 'Apollo';
import ErrorHandlerContext from 'Context/ErrorHandler.context';
import useTranslate from 'Hooks/useTranslate';
import React, { useContext, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import ButtonLoading from 'Theme/components/ButtonLoading';
import isValidEmail from 'Tools/isValidEmail';

interface EditEmailConfirmationProps {
  email: string;
  onClose: () => void;
}

function EditEmailConfirmation({ email, onClose }: Readonly<EditEmailConfirmationProps>) {
  return (
    <>
      <DialogTitle>
        <FormattedMessage id="page.userProfile.editEmail.confirmation.title" />
      </DialogTitle>
      <DialogContent>
        <DialogContentText>
          <FormattedMessage
            id="page.userProfile.editEmail.confirmation.message"
            values={{ email }}
          />
        </DialogContentText>
        <DialogContentText>
          <FormattedMessage id="page.userProfile.editEmail.confirmation.hint" />
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>
          <FormattedMessage id="common.action.close" />
        </Button>
      </DialogActions>
    </>
  );
}

interface Props {
  person: IPersonWithAccount;
  onClose: () => void;
  onCompleted: () => void;
  /** When provided, uses a direct email update (organization owner only) instead of the email change workflow */
  organizationCode?: string;
}

function EditEmailDialog({
  person,
  onCompleted,
  onClose,
  organizationCode
}: Readonly<Props>) {
  const ts = useTranslate();
  const [email, setEmail] = useState(person.email || '');
  const [error, setError] = useState<Error | null>(null);
  const errorHandler = useContext(ErrorHandlerContext);

  const [updatePersonEmail, { loading: updateLoading }] = useMutation<
    {},
    UpdatePersonEmailVariables
  >(UpdatePersonEmail, {
    onCompleted: () => {
      onCompleted();
      onClose();
    },
    onError: e => errorHandler.onError(e),
    refetchQueries: ['GetPersonWithAccount']
  });

  const [
    createEmailChangeRequest,
    { loading: createLoading, data: createData }
  ] = useMutation<{}, CreateEmailChangeRequestVariables>(
    CreateEmailChangeRequest,
    {
      onCompleted: () => onCompleted(),
      onError: e => errorHandler.onError(e),
      refetchQueries: ['GetEmailChangeRequest']
    }
  );

  const loading = organizationCode ? updateLoading : createLoading;

  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    if (loading) {
      return;
    }

    if (!isValidEmail(email)) {
      setError(new Error(ts('error.form.invalidEmail')));
      return;
    }

    if (organizationCode) {
      updatePersonEmail({
        variables: {
          personIdentifier: person.immutableId,
          organizationCode,
          newEmail: email
        }
      });
    } else {
      createEmailChangeRequest({
        variables: { personIdentifier: person.immutableId, newEmail: email }
      });
    }
  }

  if (!organizationCode && createData != null) {
    return (
      <Dialog open={true} onClose={onClose} maxWidth="xs" fullWidth={true}>
        <EditEmailConfirmation email={email} onClose={onClose} />
      </Dialog>
    );
  }

  return (
    <Dialog open={true} onClose={onClose} maxWidth="xs" fullWidth={true}>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <FormattedMessage id="page.userProfile.editEmail.editEmail" />
        </DialogTitle>
        <DialogContent>
          {organizationCode && (
            <Typography
              variant="body2"
              color="error"
              paragraph
              fontWeight="bold"
            >
              {ts('page.userProfile.editEmail.warning')}
            </Typography>
          )}
          <TextField
            name="email"
            variant="outlined"
            label={<FormattedMessage id={'page.userProfile.editEmail.email'} />}
            onChange={event => {
              setEmail(event.target.value);
              setError(null);
            }}
            value={email}
            fullWidth
            autoFocus={true}
            required
            error={Boolean(error)}
            helperText={error?.message}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            <FormattedMessage id="common.action.cancel" />
          </Button>
          <ButtonLoading type="submit" color="primary" loading={loading}>
            <FormattedMessage id="common.action.validate" />
          </ButtonLoading>
        </DialogActions>
      </form>
    </Dialog>
  );
}

export default EditEmailDialog;
